import { css } from '@emotion/react';
import type { Cell } from '@tanstack/react-table';

import { HighlightText } from '@adflow/frontend/ui-common';
import { su, theme } from '@adflow/frontend/ui-theme';
import { useHighlightMetaSafe } from '@adflow/frontend/util-common';
import type { DataTableItemType } from '@adflow/shared/util-column-templates';

import { useExtendedSearchMatch } from './useExtendedSearchMatch';

const styles = {
	attributeTag: css(su.roundedMd, su.text2xs, {
		backgroundColor: theme.colors.background.dropdown.selected,
		display: 'flex',
		alignItems: 'center',
		paddingLeft: '0.25rem',
		paddingRight: '0.25rem',
		lineHeight: '1.25rem',
		cursor: 'pointer',
	}),
};

interface ExtendedSearchProps<T> {
	type: DataTableItemType;
	data: T;
	cells: Cell<T, unknown>[];
}

export function ExtendedSearch<T extends object>({
	type,
	data,
	cells,
}: ExtendedSearchProps<T>) {
	const highlightedMeta = useHighlightMetaSafe();

	const highlightedText = highlightedMeta?.highlightedText;

	const searchMatch = useExtendedSearchMatch({
		type,
		search: highlightedText ?? '',
		data,
		cells,
	});

	if (!searchMatch) return null;

	return (
		<div
			css={[
				styles.attributeTag,
				{
					backgroundColor: theme.colors.background.tableHeader,
					display: 'inline-block',
				},
			]}
		>
			<HighlightText
				css={[su.textXs, { whiteSpace: 'nowrap' }]}
				text={searchMatch}
				highlight={highlightedText}
			/>
		</div>
	);
}
